import React from 'react';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { trackNow } from 'shared/models/analytic';
import View from 'components/ui/view';
import Text from 'components/ui/text';
import Card from 'components/ui/card';
import Button from 'components/ui/button';
import LoadingSpinner from 'components/ui/loadingSpinner';
import Cart from 'images/icons/cart.svg?react-svg';
import Summary from './summary';

export default function SubstitutionsOverview({
  user,
  baseUrl,
  contactNumber,
  items,
  updates,
  removeItem,
  cancelUpdate,
  confirmUpdate,
  loadingStates,
  errors
}) {
  const isPlural = items.length > 1;
  const hasUpdates = !_.isEmpty(updates);
  const disabled = _.some(items, item => _.isEmpty(updates[item.sku_id]));

  return (
    <div className="substitutions__container">
      <View margin="sm">
        <Card>
          <View padding="md" margin="sm" className="text-center">
            <div className="substitutions__cart-icon-container inline-block">
              <span className="substitutions__reject-icon reject-icon" />
              <Cart width={45} />
            </div>
            <p>
              Hey{user && user.firstname ? ` ${user.firstname}` : ''}, looks
              like
              {isPlural
                ? ' some items you ordered are '
                : ' an item you ordered is '}
              unavailable. Please choose
              {isPlural ? ' substitutions ' : ' a substitution '}
              or remove
              {isPlural ? ' the unavailable items ' : ' the item '}
              from your order. Cheers.
            </p>
          </View>
        </Card>
      </View>

      {items.map((item, idx) => (
        <OverviewItem
          key={`${item.sku_id}-${idx}`}
          item={item}
          itemUpdates={updates[item.sku_id] || {}}
          loading={loadingStates[item.sku_id]}
          error={errors[item.sku_id]}
          baseUrl={baseUrl}
          removeItem={removeItem}
          cancelUpdate={cancelUpdate}
        />
      ))}

      {hasUpdates && <OverviewSubtotal updates={updates} />}

      <View margin="sm" padding={{ top: 'sm', bottom: 'sm', sides: 'md' }}>
        <div>
          <Button
            primary
            fullWidth
            textStyles={{ size: 'xs', weight: 'med' }}
            onClick={confirmUpdate}
            disabled={disabled}
            loading={loadingStates.submit}
          >
            Update Order
          </Button>
        </div>
        <div>
          {contactNumber && (
            <a href={`tel:${contactNumber}`}>
              <Button
                secondary
                fullWidth
                outline
                textStyles={{ size: 'xs', weight: 'med' }}
                onClick={() => trackNow('Substitution Support Clicked')}
              >
                Contact Support
              </Button>
            </a>
          )}
        </div>
        {errors.submit && (
          <Text error className="text-center">
            {errors.submit}
          </Text>
        )}
      </View>
    </div>
  );
}

function OverviewItem({
  item,
  itemUpdates,
  baseUrl,
  removeItem,
  cancelUpdate,
  loading,
  error
}) {
  const availableQty = item.stock_level || 0;
  const unavailableQty = item.quantity - availableQty;
  const removedItem = itemUpdates.removed;
  const addedItem = itemUpdates.added;

  return (
    <View margin="sm">
      <Card className="substitutions-overview__item">
        {loading && <LoadingSpinner green />}

        <View padding="sm">
          {addedItem && (
            <View margin="md">
              <Summary item={addedItem} />
            </View>
          )}

          <View margin="md">
            <Summary item={removedItem || item} removed={!!removedItem} />
          </View>

          <div>
            <Link
              to={{
                pathname: `${baseUrl}/${item.sku_id}`,
                state: { from: 'overview' }
              }}
            >
              <Button
                primary
                fullWidth
                outline
                textStyles={{ size: 'xs', weight: 'med' }}
              >
                {removedItem ? 'Edit Substitution' : 'Substitute'}
              </Button>
            </Link>
          </div>

          <div>
            <Button
              secondary
              fullWidth
              outline
              textStyles={{ size: 'xs', weight: 'med' }}
              onClick={
                removedItem ? () => cancelUpdate(item) : () => removeItem(item)
              }
            >
              {removedItem
                ? 'Cancel'
                : `Remove${availableQty ? ` ${unavailableQty}` : ''}`}
            </Button>
          </div>

          {error && (
            <Text error className="text-center">
              {error}
            </Text>
          )}
        </View>
      </Card>
    </View>
  );
}

function productSubtotal(product = {}) {
  const price = product.price || product.discount_price || 0;
  const quantity = product.quantity || 0;
  return price * quantity;
}

function calculateAdjustment(itemUpdates = {}) {
  const removedSubtotal = productSubtotal(itemUpdates.removed);
  const addedSubtotal = productSubtotal(itemUpdates.added);
  return addedSubtotal - removedSubtotal;
}

function OverviewSubtotal({ updates }) {
  const subtotalDiff =
    _.keys(updates).reduce((sum, sku) => {
      return sum + calculateAdjustment(updates[sku]);
    }, 0) || 0;

  const displayDiff = Math.abs(subtotalDiff).toFixed(2);

  return (
    <View margin="sm">
      <Card>
        <View padding="sm">
          <Text success weight="bold" className="right">
            {subtotalDiff <= 0 ? `(-$${displayDiff})` : `+$${displayDiff}`}
          </Text>
          <Text weight="bold">Change in Order Subtotal</Text>
          <Text
            weight="light"
            secondary
            tag="small"
            className="text-emphasized"
          >
            Subject to taxes and fees.
          </Text>
        </View>
      </Card>
    </View>
  );
}
